import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, Shield, Zap, Check, ArrowRight, AlertTriangle, Target,
  BarChart2, Award, ExternalLink, CheckCircle, LineChart, Bot,
  Play, Lock, RefreshCw, Headphones, Gift, Building2, Clock,
  BookOpen, GraduationCap, FileText, ChevronDown, ChevronUp,
  DollarSign, Calendar, Percent, Activity, Users, Globe,
  ArrowUpRight, ArrowDownRight, Minus, HelpCircle, Layers,
  Gauge, Rocket, ShieldCheck, TrendingDown, Settings, Package, Info, BarChart3,
  Download, X, User, Mail, Loader2, MessageCircle, Send, Calculator, Video
} from 'lucide-react';
import { ParticlesBackground } from '../components/ParticlesBackground';
import { OrbAlgoSubscribeModal } from '../components/OrbAlgoSubscribeModal';
import { OrbBreakoutAnimation } from '../components/OrbBreakoutAnimation';
import { GetFundedCalculator } from '../components/GetFundedCalculator';
import { CountryPhoneInput, COUNTRIES } from '../components/CountryPhoneInput';
import { LiveBacktestVideo } from '../components/LiveBacktestVideo';
import { YouTubeVideo } from '../components/YouTubeVideo';
import { EngineComparison } from '../components/EngineComparison';
import { ComparisonTable } from '../components/ComparisonTable';
import { CallBand } from '../components/CallBand';
import { BookCallSection } from '../components/BookCallSection';
import { BookCallModal } from '../components/BookCallModal';
import { 
  V1_STATS, V2_STATS, COMBINED_STATS, ASSETS, COPY, FAQ_DATA, 
  getStats, formatNumber, formatPercent, interpolatePassRate, ORB_VIDEOS 
} from '../config/orbConfig';

// ORB Algo Backtest Screenshots
// Screenshots organized by version
// Screenshot Mapping - Derived from config
const SCREENSHOTS = {
  V1: ASSETS.v1.screenshots,
  V2: ASSETS.v2.screenshots,
};

// Live Trade Snapshots
const TRADE_SNAPSHOTS = [
  {
    id: 1,
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/fe0dao60_image.png",
    instrument: "NAS100",
    type: "Long & Short",
    date: "Jul 16-22, 2026",
    description: "Multiple ORB setups showing long and short entries with clear TP/SL levels"
  },
  {
    id: 2,
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/zgr9uscv_image.png",
    instrument: "NAS100",
    type: "Mixed",
    date: "Jul 2-11, 2026",
    description: "Series of ORB trades with winning and losing setups marked"
  }
];

// Monk Algo ORB VARIATIONS - Derived from config for backwards compatibility
const ORB_VARIATIONS = [
  {
    id: 'v1',
    name: V1_STATS.fullName,
    version: 'V1',
    instrument: 'MNQ (Micro NAS100)',
    riskLevel: V1_STATS.label,
    riskColor: V1_STATS.color,
    bestFor: 'Traders who want the calmest ride',
    tradingStyle: V1_STATS.tagline,
    description: V1_STATS.description,
    useCase: 'Choose it when staying far from a drawdown limit matters most.',
    icon: ShieldCheck,
    stats: {
      netProfit: `+$${formatNumber(V1_STATS.oneYear.netProfit, 0)}`,
      totalReturn: `+${V1_STATS.oneYear.totalReturn}%`,
      winRate: `${V1_STATS.oneYear.winRate}%`,
      profitFactor: V1_STATS.oneYear.profitFactor.toString(),
      maxDrawdown: `${V1_STATS.oneYear.maxDrawdown}%`,
      totalTrades: V1_STATS.oneYear.totalTrades,
      profitFactor: V1_STATS.oneYear.profitFactor.toString(),
      grossProfit: V1_STATS.oneYear.grossProfit,
      grossLoss: V1_STATS.oneYear.grossLoss,
      winningTrades: V1_STATS.oneYear.winningTrades,
      losingTrades: V1_STATS.oneYear.losingTrades
    },
    pdfUrl: ASSETS.v1.pdfReport,
    fiveYearCsvUrl: ASSETS.v1.csv5Year,
    features: [
      `Highest win rate (${V1_STATS.oneYear.winRate}%)`, 
      `Lowest drawdown (${V1_STATS.oneYear.maxDrawdown}%)`, 
      `Best profit factor (${V1_STATS.oneYear.profitFactor})`
    ]
  },
  {
    id: 'v2',
    name: V2_STATS.fullName,
    version: 'V2',
    instrument: 'MNQ (Micro NAS100)',
    riskLevel: V2_STATS.label,
    riskColor: V2_STATS.color,
    bestFor: 'Traders who want the most action & total return',
    tradingStyle: V2_STATS.tagline,
    description: V2_STATS.description,
    useCase: 'Choose it when you want more opportunities and total return.',
    icon: TrendingUp,
    stats: {
      netProfit: `+$${formatNumber(V2_STATS.oneYear.netProfit, 0)}`,
      totalReturn: `+${V2_STATS.oneYear.totalReturn}%`,
      winRate: `${V2_STATS.oneYear.winRate}%`,
      profitFactor: V2_STATS.oneYear.profitFactor.toString(),
      maxDrawdown: `${V2_STATS.oneYear.maxDrawdown}%`,
      totalTrades: V2_STATS.oneYear.totalTrades,
      profitFactor: V2_STATS.oneYear.profitFactor.toString(),
      grossProfit: V2_STATS.oneYear.grossProfit,
      grossLoss: V2_STATS.oneYear.grossLoss,
      winningTrades: V2_STATS.oneYear.winningTrades,
      losingTrades: V2_STATS.oneYear.losingTrades
    },
    pdfUrl: ASSETS.v2.pdfReport,
    fiveYearCsvUrl: ASSETS.v2.csv5Year,
    features: [
      `Highest returns (+${V2_STATS.oneYear.totalReturn}%)`, 
      `Strong win rate (${V2_STATS.oneYear.winRate}%)`, 
      `More trades (${V2_STATS.oneYear.totalTrades})`
    ]
  },
];

// Monthly P&L Data - Derived from config
const MONTHLY_PNL_DATA = {
  V1: V1_STATS.monthlyPnl,
  V2: V2_STATS.monthlyPnl,
};

// REAL Monthly Performance Data from CSV (Jul 2025 - Jul 2026)
const MONTHLY_PERFORMANCE = [
  { month: "Jul 2025", profit: 293.77, trades: 22, wins: 11, losses: 11, winRate: 50.0 },
  { month: "Aug 2025", profit: 712.73, trades: 36, wins: 18, losses: 18, winRate: 50.0 },
  { month: "Sep 2025", profit: 213.91, trades: 40, wins: 20, losses: 20, winRate: 50.0 },
  { month: "Oct 2025", profit: 846.81, trades: 52, wins: 26, losses: 26, winRate: 50.0 },
  { month: "Nov 2025", profit: 598.46, trades: 46, wins: 23, losses: 23, winRate: 50.0 },
  { month: "Dec 2025", profit: 306.54, trades: 30, wins: 15, losses: 15, winRate: 50.0 },
  { month: "Jan 2026", profit: 429.63, trades: 32, wins: 16, losses: 16, winRate: 50.0 },
  { month: "Feb 2026", profit: 697.46, trades: 36, wins: 18, losses: 18, winRate: 50.0 },
  { month: "Mar 2026", profit: 650.03, trades: 44, wins: 22, losses: 22, winRate: 50.0 },
  { month: "Apr 2026", profit: 467.53, trades: 40, wins: 20, losses: 20, winRate: 50.0 },
  { month: "May 2026", profit: 624.34, trades: 54, wins: 27, losses: 27, winRate: 50.0 },
  { month: "Jun 2026", profit: 753.61, trades: 54, wins: 27, losses: 27, winRate: 50.0 },
  { month: "Jul 2026", profit: 895.74, trades: 34, wins: 17, losses: 17, winRate: 50.0 },
];

// Calculate totals from real data
const TOTAL_PROFIT = MONTHLY_PERFORMANCE.reduce((sum, m) => sum + m.profit, 0);
const TOTAL_TRADES = MONTHLY_PERFORMANCE.reduce((sum, m) => sum + m.trades, 0);
const AVG_MONTHLY = TOTAL_PROFIT / MONTHLY_PERFORMANCE.length;

// ORB Algo Performance Stats
const PERFORMANCE_STATS = {
  totalReturn: 83.61,
  totalPnL: 8361.40,
  maxDrawdown: 7.95,
  winningTrades: 105,
  losingTrades: 66,
  totalTrades: 171,
  winRate: 61.40,
  profitFactor: 1.513,
  startingBalance: 10000,
  finalBalance: 18361.40,
  profitableMonths: 13,
  totalMonths: 13,
};

// What's Included in the subscription
const WHATS_INCLUDED = [
  { icon: Layers, text: 'Both Monk Algo ORB Variations (V1–V2)', highlight: true },
  { icon: Bot, text: 'MT5 Expert Advisor' },
  { icon: LineChart, text: 'NinjaTrader 8 Strategy' },
  { icon: FileText, text: 'Full Backtest Reports' },
  { icon: Globe, text: 'NAS100 Optimized' },
  { icon: Gift, text: 'Future ORB Variations (V3, V4...)' },
  { icon: RefreshCw, text: 'Continuous Strategy Improvements' },
  { icon: Headphones, text: 'Ongoing Support' },
];

// Why different traders need different variations
const WHY_VARIATIONS = [
  {
    icon: Shield,
    title: 'Capital Preservers',
    description: 'Prioritize protecting capital with steady, reliable returns.',
    solution: 'V1 offers ultra-low 2.51% drawdown with 77% win rate and 2.93 profit factor.'
  },
  {
    icon: TrendingUp,
    title: 'Growth Seekers',
    description: 'Want maximum returns and can handle higher volatility.',
    solution: `V2 delivers +${V2_STATS.oneYear.totalReturn}%+ returns with more trading opportunities (${V2_STATS.oneYear.totalTrades} trades).`
  },
  {
    icon: Building2,
    title: 'Prop Firm Traders',
    description: 'Need strict drawdown control for evaluation challenges.',
    solution: 'V1 has just 2.51% max DD with best profit factor (2.93) and 77% win rate.'
  },
  {
    icon: Target,
    title: 'Index Traders',
    description: 'Prefer trading stock indices with high liquidity.',
    solution: 'Both variations are optimized for NAS100 with backtested performance data.'
  },
  {
    icon: Activity,
    title: 'Active Traders',
    description: 'Want more trading opportunities and frequent signals.',
    solution: 'Both V1 and V2 are optimized for NAS100 with excellent win rates.'
  },
];

// FAQ Data imported from config - see /src/config/orbConfig.js

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "", prefix = "", decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(current);
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return (
    <span ref={ref}>
      {prefix}{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
    </span>
  );
};

// ORB Variation Card Component - Reusable and Scalable with Stats & Download
const OrbVariationCard = ({ variation, index }) => {
  const IconComponent = variation.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group relative rounded-2xl border border-white/10 bg-[#0a0a12]/80 backdrop-blur-sm overflow-hidden hover:border-white/20 transition-all duration-300"
    >
      {/* Version Badge */}
      <div 
        className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold"
        style={{ backgroundColor: `${variation.riskColor}20`, color: variation.riskColor }}
      >
        {variation.version}
      </div>
      
      <div className="p-6">
        {/* Icon & Name */}
        <div className="flex items-start gap-4 mb-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${variation.riskColor}15` }}
          >
            <IconComponent className="w-6 h-6" style={{ color: variation.riskColor }} />
          </div>
          <div>
            <h3 className="font-heading font-bold text-white text-lg">{variation.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span 
                className="text-xs font-medium px-2 py-0.5 rounded"
                style={{ backgroundColor: `${variation.riskColor}20`, color: variation.riskColor }}
              >
                {variation.riskLevel}
              </span>
              <span className="text-white/40 text-xs">{variation.instrument}</span>
            </div>
          </div>
        </div>
        
        {/* Performance Stats Grid */}
        {variation.stats && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {/* Top Row - Return & Win Rate */}
            <div className="text-center p-3 rounded-xl bg-white/5">
              <div className="text-[#10B981] font-heading font-bold text-sm">{variation.stats.totalReturn}</div>
              <div className="text-white/40 text-[10px]">Return</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-white/5">
              <div className="text-[#06b6d4] font-heading font-bold text-sm">{variation.stats.winRate}</div>
              <div className="text-white/40 text-[10px]">Win Rate</div>
            </div>
            {/* Bottom Row - Drawdown (Highlighted) & Profit Factor */}
            <div className="text-center p-3 rounded-xl bg-gradient-to-r from-[#F59E0B]/20 to-[#F59E0B]/10 border border-[#F59E0B]/30">
              <div className="text-[#F59E0B] font-heading font-bold text-sm">{variation.stats.maxDrawdown}</div>
              <div className="text-[#F59E0B]/70 text-[10px] font-medium">Max Drawdown</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-gradient-to-r from-[#8B5CF6]/20 to-[#8B5CF6]/10 border border-[#8B5CF6]/30">
              <div className="text-[#8B5CF6] font-heading font-bold text-sm">{variation.stats.profitFactor}</div>
              <div className="text-[#8B5CF6]/70 text-[10px] font-medium">Profit Factor</div>
            </div>
          </div>
        )}
        
        {/* Best For & Style */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/5 rounded-lg p-3">
            <div className="text-white/40 text-xs mb-1">Best For</div>
            <div className="text-white text-sm font-medium">{variation.bestFor}</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3">
            <div className="text-white/40 text-xs mb-1">Trading Style</div>
            <div className="text-white text-sm font-medium">{variation.tradingStyle}</div>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          {variation.description}
        </p>
        
        {/* Features */}
        <div className="space-y-2 mb-4">
          {variation.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check size={14} style={{ color: variation.riskColor }} />
              <span className="text-white/60 text-xs">{feature}</span>
            </div>
          ))}
        </div>
        
        {/* Download Report Button */}
        {variation.pdfUrl && (
          <a
            href={variation.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 rounded-xl border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm font-medium flex items-center justify-center gap-2 transition-all mb-3"
          >
            <FileText size={16} style={{ color: variation.riskColor }} />
            View 1-Year Backtest Report
            <ExternalLink size={12} className="text-white/40" />
          </a>
        )}
        
        {/* 5-Year Backtest CSV Download - Premium CTA */}
        {variation.fiveYearCsvUrl && (
          <a
            href={variation.fiveYearCsvUrl}
            download
            className="w-full py-3 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:from-[#F59E0B]/90 hover:to-[#EF4444]/90 text-white text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#F59E0B]/20"
          >
            <Download size={16} />
            Download 5-Year Backtest Data
            <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">CSV</span>
          </a>
        )}
      </div>
    </motion.div>
  );
};

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const stagger = { animate: { transition: { staggerChildren: 0.1 } } };

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tester Lead Capture Modal Component
const TesterLeadModal = ({ isOpen, onClose, platform = 'nt8' }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [countryCode, setCountryCode] = useState('IN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  
  const platformName = 'NinjaTrader 8';

  // Auto-detect country on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneToCountry = {
          'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN', 'Asia/Karachi': 'PK',
          'Asia/Dhaka': 'BD', 'Asia/Colombo': 'LK', 'Asia/Kathmandu': 'NP',
          'America/New_York': 'US', 'America/Los_Angeles': 'US', 'America/Chicago': 'US',
          'Europe/London': 'GB', 'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA',
          'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Jakarta': 'ID',
          'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN', 'Asia/Manila': 'PH',
          'Asia/Shanghai': 'CN', 'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR',
          'Australia/Sydney': 'AU', 'America/Toronto': 'CA', 'Europe/Berlin': 'DE',
          'Europe/Paris': 'FR', 'Europe/Rome': 'IT', 'Europe/Madrid': 'ES',
          'Pacific/Auckland': 'NZ', 'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG',
        };
        if (timezoneToCountry[timezone]) {
          setCountryCode(timezoneToCountry[timezone]);
          return;
        }
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          const detected = data.country_code?.toUpperCase();
          if (COUNTRIES.find(c => c.code === detected)) setCountryCode(detected);
        }
      } catch (e) {
        setCountryCode('IN');
      }
    };
    if (isOpen && !success) detectCountry();
  }, [isOpen, success]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setError('All fields are required');
      return;
    }
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 8) {
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/orb-algo/tester/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: phoneDigits,
          country_code: countryCode,
          platform: platform,
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
        setRedirectUrl(data.redirect_url);
        // Open Google Drive in new tab
        window.open(data.redirect_url, '_blank');
      } else {
        setError(data.detail || 'Failed to process request');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '' });
    setError('');
    setSuccess(false);
    setRedirectUrl('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-md bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
      >
        {!success ? (
          <>
            {/* Header */}
            <div className={`p-6 border-b border-white/10 bg-gradient-to-r ${platform === 'nt8' ? 'from-[#06b6d4]/10 to-[#0891b2]/10' : 'from-[#F59E0B]/10 to-[#EF4444]/10'}`}>
              <button onClick={handleClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center">
                <X size={16} className="text-white/60" />
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${platform === 'nt8' ? 'bg-[#06b6d4]/20' : 'bg-[#F59E0B]/20'}`}>
                  <Download size={20} className={platform === 'nt8' ? 'text-[#06b6d4]' : 'text-[#F59E0B]'} />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-lg text-white">Download {platformName} Tester</h2>
                  <p className="text-white/50 text-xs">Verify our backtest results yourself</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">Full Name *</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F59E0B]/50"
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-white/50 mb-1.5 block">Email *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F59E0B]/50"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <CountryPhoneInput
                countryCode={countryCode}
                setCountryCode={setCountryCode}
                phone={formData.phone}
                setPhone={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                label="WhatsApp Number *"
                testIdPrefix="tester-lead"
              />

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3.5 rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-70 ${platform === 'nt8' ? 'bg-gradient-to-r from-[#06b6d4] to-[#0891b2]' : 'bg-gradient-to-r from-[#F59E0B] to-[#EF4444]'}`}
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                {loading ? 'Processing...' : 'Get Download Link'}
              </button>

              <p className="text-[10px] text-white/40 text-center">
                We'll send the files to this email and may contact you on WhatsApp about Monk Algo.
              </p>
            </form>
          </>
        ) : (
          /* Success State */
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-[#10B981]" />
            </div>
            <h3 className="font-heading font-bold text-xl text-white mb-2">Your Download is Ready!</h3>
            <p className="text-white/60 text-sm mb-6">
              The tester files and PDF guide have opened in a new tab. If not, click the button below.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <a
                href={redirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90"
              >
                <Download size={18} />
                Open Download Folder
              </a>

              <a
                href="https://telegram.openinapp.co/ud84q"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 bg-[#0088cc]/20 border border-[#0088cc]/40 rounded-xl text-[#0088cc] font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#0088cc]/30"
              >
                <Send size={18} />
                Join Our Telegram
              </a>

              <button
                onClick={() => {
                  handleClose();
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full py-3 bg-white/5 border border-white/10 rounded-xl text-white/70 font-heading font-medium text-sm flex items-center justify-center gap-2 hover:bg-white/10"
              >
                <ArrowRight size={18} />
                Get the Full ORB Algo
              </button>
            </div>

            <button onClick={handleClose} className="mt-4 text-white/40 text-xs hover:text-white/60">
              Close
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default function OrbAlgoPage() {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  const [showTesterModal, setShowTesterModal] = useState(null); // null or 'nt8'
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [activeScreenshot, setActiveScreenshot] = useState('overview');
  const [activeScreenshotVersion, setActiveScreenshotVersion] = useState('V1');
  const [activeTradeSnapshot, setActiveTradeSnapshot] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedPnlVariation, setSelectedPnlVariation] = useState('V1');
  const [heroVersion, setHeroVersion] = useState('V1');

  const handleBookCall = () => setShowBookCallModal(true);

  const handleSubscribe = (plan = 'yearly') => {
    setSelectedPlan(plan);
    setShowSubscribeModal(true);
  };
  
  // Get current variation data for hero stats
  const currentHeroStats = heroVersion === 'V1' ? V1_STATS : V2_STATS;

  const screenshotTabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'performance', label: 'Performance', icon: BarChart2 },
    { id: 'trades', label: 'Trades', icon: Target },
  ];

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Call Band - Below Header */}
      <div className="pt-20">
        <CallBand onBookCall={handleBookCall} />
      </div>
      
      {/* Hero Section - Repositioned for Collection */}
      <section className="relative pt-8 pb-16 overflow-hidden" data-testid="orb-hero-section">
        <div className="absolute inset-0 z-0"><ParticlesBackground /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#7c3aed]/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          {/* Premium Badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[#7c3aed]/30">
              <span className="text-white/60 text-xs font-mono uppercase tracking-wider">{COPY.hero.eyebrow}</span>
            </div>
          </motion.div>

          {/* Main Hero Content */}
          <motion.div initial="initial" animate="animate" variants={stagger} className="text-center max-w-4xl mx-auto">
            <motion.h1 variants={fadeUp} className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl mb-4 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#06b6d4]">Pass challenges.</span>
              <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#EF4444]"> Grow your capital.</span>
              <br className="hidden sm:block" />
              <span className="text-white"> Both engines do </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7c3aed] to-[#06b6d4]">both.</span>
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-base md:text-lg text-white/60 mb-8 max-w-2xl mx-auto leading-relaxed">
              {COPY.hero.subhead}
            </motion.p>

            {/* What's Included Pills */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-3 mb-8">
              {[
                { icon: Bot, text: 'MT5 Expert Advisor' },
                { icon: LineChart, text: 'NinjaTrader 8 Strategy' },
                { icon: Globe, text: 'NAS100/MNQ Optimized' },
                { icon: RefreshCw, text: '2 Activations Included' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <item.icon size={14} className="text-[#06b6d4]" />
                  <span className="text-white/70 text-xs">{item.text}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons - Primary + Secondary (Tester) */}
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <a 
                href="#pricing"
                className="w-full sm:w-auto bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] hover:opacity-90 text-white font-heading font-semibold text-base px-8 py-4 rounded-xl flex items-center justify-center gap-2 group transition-all shadow-lg shadow-[#7c3aed]/25"
                data-testid="orb-hero-subscribe-btn"
              >
                <Zap size={18} className="group-hover:animate-pulse" />
                {COPY.hero.ctaPrimary}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href="#verify" 
                className="w-full sm:w-auto bg-white/5 border border-white/20 hover:bg-white/10 text-white font-heading font-medium text-base px-8 py-4 rounded-xl flex items-center justify-center gap-2 transition-all"
                data-testid="orb-hero-tester-btn"
              >
                <Download size={18} className="text-[#F59E0B]" />
                {COPY.hero.ctaSecondary}
              </a>
            </motion.div>

            {/* Key Stats - Dynamic based on selected version */}
            <motion.div variants={fadeUp} className="max-w-3xl mx-auto">
              {/* Version Selector Tabs */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {['V1', 'V2'].map((version) => {
                  const vStats = version === 'V1' ? V1_STATS : V2_STATS;
                  const isActive = heroVersion === version;
                  return (
                    <button
                      key={version}
                      onClick={() => setHeroVersion(version)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] shadow-lg'
                          : 'bg-white/10 border border-white/20 hover:bg-white/20'
                      }`}
                      data-testid={`hero-version-${version}`}
                    >
                      <span className={isActive ? 'text-white' : 'text-white'}>{version}</span>
                      <span className="mx-1 text-white/60">·</span>
                      <span style={{ color: isActive ? '#ffffff' : vStats.color }} className="font-semibold">
                        {vStats.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* Dynamic Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-heading font-bold text-2xl md:text-3xl" style={{ color: currentHeroStats.color }}>
                    +{currentHeroStats.oneYear.totalReturn}%
                  </div>
                  <div className="text-white/70 text-xs font-medium">Backtested Return</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-heading font-bold text-2xl md:text-3xl text-[#06b6d4]">
                    {currentHeroStats.oneYear.winRate}%
                  </div>
                  <div className="text-white/70 text-xs font-medium">Win Rate</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-heading font-bold text-2xl md:text-3xl text-[#F59E0B]">
                    {currentHeroStats.oneYear.maxDrawdown}%
                  </div>
                  <div className="text-white/70 text-xs font-medium">Max Drawdown</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-heading font-bold text-2xl md:text-3xl text-white">
                    {currentHeroStats.oneYear.profitFactor}
                  </div>
                  <div className="text-white/70 text-xs font-medium">Profit Factor</div>
                </div>
              </div>
              
              {/* Version Info */}
              <div className="text-center mt-3">
                <span className="text-white/40 text-xs">
                  {currentHeroStats.fullName} · MNQ (Micro NAS100) · {currentHeroStats.oneYear.totalTrades} trades
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* 3D ORB Breakout Animation */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-12"
            data-testid="orb-hero-3d-animation"
          >
            <OrbBreakoutAnimation />
          </motion.div>
        </div>
      </section>
      
      {/* Meet the Two Engines - Side by Side Cards */}
      <EngineComparison />
      
      {/* Head-to-Head Comparison Table */}
      <ComparisonTable />

      {/* Pass Probability Section - Monte Carlo Simulations */}
      <section className="py-20 relative overflow-hidden" id="pass-probability" data-testid="orb-pass-probability-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#7c3aed]/5 to-transparent" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          {/* Section Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F59E0B]/20 to-[#06b6d4]/20 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-mono mb-4">
              <BarChart3 size={14} />
              MONTE CARLO ANALYSIS
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-white mb-4">
              Will It Pass Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#06b6d4]">Prop Firm Challenge?</span>
            </h2>
            <p className="text-white/70 text-xl font-medium mb-6">
              We didn't test it once. We tested it <span className="text-[#F59E0B]">50,000 times.</span>
            </p>
          </motion.div>

          {/* Monte Carlo Explanation */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="glass rounded-2xl p-6 border border-white/10 mb-8 max-w-4xl mx-auto"
          >
            <p className="text-white/60 text-sm leading-relaxed">
              A backtest shows one path through history. In live trading, the same trades can arrive in a different order, and order decides whether you hit the target or the drawdown first. A <span className="text-white font-medium">Monte Carlo simulation</span> reshuffles the strategy's real trades into 50,000 possible sequences and counts how many reach the profit target before breaching max drawdown. That percentage is the <span className="text-[#F59E0B] font-medium">pass probability</span> — a more honest measure than a single backtest.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-white/40 text-xs">
              <Info size={12} />
              <span>Based on 5-year backtested trades. MNQ Futures (V1 = {V1_STATS.fiveYear.totalTrades} trades, V2 = {formatNumber(V2_STATS.fiveYear.totalTrades, 0)} trades, {V1_STATS.fiveYear.dateRange}). {V1_STATS.monteCarlo.simulations.toLocaleString()} simulations per configuration.</span>
            </div>
          </motion.div>

          {/* Key Message - Risk Equivalence Callout */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-block glass rounded-xl px-6 py-4 border border-[#7c3aed]/30">
              <p className="text-white/80 text-sm">
                <span className="text-[#7c3aed] font-medium">Key insight:</span> {COPY.monteCarlo.callout}
              </p>
            </div>
          </motion.div>

          {/* Two Area Tabs/Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Futures Prop Firms Area */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 border border-[#F59E0B]/20"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                  <TrendingUp size={20} className="text-[#F59E0B]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-white">Futures Prop Firms</h3>
                  <p className="text-[#F59E0B] text-xs font-medium">NinjaTrader 8 → Direct Execution</p>
                </div>
              </div>
              <div className="mb-4 p-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                <p className="text-[#F59E0B] text-xs font-mono text-center">+6% profit target · 4% max drawdown · single phase</p>
              </div>
              <div className="flex flex-wrap gap-1 mb-4">
                {['Lucid Trading', 'FundedNext Futures', 'Tradeify'].map((firm) => (
                  <span key={firm} className="px-2 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-medium border border-[#F59E0B]/20">
                    {firm}
                  </span>
                ))}
              </div>

              {/* V1 Futures Table */}
              <div className="mb-4">
                <h4 className="text-white/70 text-xs font-medium mb-2">V1 · {V1_STATS.label} (MNQ) — {V1_STATS.fiveYear.totalTrades} trades over 5 years</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-white/40 border-b border-white/10">
                        <th className="text-left py-2 px-2">Risk/trade</th>
                        <th className="text-center py-2 px-2">Pass %</th>
                        <th className="text-center py-2 px-2">Fail %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-white/5">
                        <td className="py-2 px-2 text-white/60">0.25%</td>
                        <td className="py-2 px-2 text-center text-[#10B981] font-medium">100.0%</td>
                        <td className="py-2 px-2 text-center text-white/40">0.0%</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-2 px-2 text-white/60">0.50%</td>
                        <td className="py-2 px-2 text-center text-[#10B981] font-medium">99.7%</td>
                        <td className="py-2 px-2 text-center text-white/40">0.3%</td>
                      </tr>
                      <tr className="bg-[#F59E0B]/10 rounded">
                        <td className="py-2 px-2 text-[#F59E0B] font-medium">0.75% <span className="text-[10px]">(Recommended)</span></td>
                        <td className="py-2 px-2 text-center text-[#10B981] font-medium">98.7%</td>
                        <td className="py-2 px-2 text-center text-white/40">1.3%</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-2 px-2 text-white/60">1.00%</td>
                        <td className="py-2 px-2 text-center text-[#10B981] font-medium">87.9%</td>
                        <td className="py-2 px-2 text-center text-white/40">12.1%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* V2 Futures Table */}
              <div>
                <h4 className="text-white/70 text-xs font-medium mb-2">V2 · {V2_STATS.label} (MNQ) — {formatNumber(V2_STATS.fiveYear.totalTrades, 0)} trades over 5 years</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-white/40 border-b border-white/10">
                        <th className="text-left py-2 px-2">Risk/trade</th>
                        <th className="text-center py-2 px-2">Pass %</th>
                        <th className="text-center py-2 px-2">Fail %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-white/5">
                        <td className="py-2 px-2 text-white/60">0.25%</td>
                        <td className="py-2 px-2 text-center text-[#10B981] font-medium">100.0%</td>
                        <td className="py-2 px-2 text-center text-white/40">0.0%</td>
                      </tr>
                      <tr className="bg-[#F59E0B]/10 rounded">
                        <td className="py-2 px-2 text-[#F59E0B] font-medium">0.50% <span className="text-[10px]">(Recommended)</span></td>
                        <td className="py-2 px-2 text-center text-[#10B981] font-medium">98.9%</td>
                        <td className="py-2 px-2 text-center text-white/40">1.1%</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-2 px-2 text-white/60">0.75%</td>
                        <td className="py-2 px-2 text-center text-[#d97706] font-medium">78.9%</td>
                        <td className="py-2 px-2 text-center text-white/40">21.1%</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-2 px-2 text-white/60">1.00%</td>
                        <td className="py-2 px-2 text-center text-[#EF4444] font-medium">33.2%</td>
                        <td className="py-2 px-2 text-center text-white/40">66.8%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>

            {/* Forex / CFD Prop Firms Area */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 border border-[#06b6d4]/20"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#06b6d4]/20 flex items-center justify-center">
                  <Bot size={20} className="text-[#06b6d4]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-white">Forex / CFD Prop Firms</h3>
                  <p className="text-[#06b6d4] text-xs font-medium">MT5 Expert Advisor · 2-Step Challenge</p>
                </div>
              </div>
              <div className="mb-4 p-2 rounded-lg bg-[#06b6d4]/10 border border-[#06b6d4]/20">
                <p className="text-[#06b6d4] text-xs font-mono text-center">Step 1: +8% target · Step 2: +6% target · 10% max DD</p>
              </div>
              <div className="flex flex-wrap gap-1 mb-4">
                {['Blueberry Funded', 'The5ers'].map((firm) => (
                  <span key={firm} className="px-2 py-1 rounded-full bg-[#06b6d4]/10 text-[#06b6d4] text-[10px] font-medium border border-[#06b6d4]/20">
                    {firm}
                  </span>
                ))}
              </div>

              {/* V1 CFD Tables */}
              <div className="mb-4">
                <h4 className="text-white/70 text-xs font-medium mb-2">V1 · {V1_STATS.label} — {V1_STATS.fiveYear.totalTrades} trades</h4>
                <div className="grid grid-cols-2 gap-2">
                  {/* Step 1 */}
                  <div>
                    <p className="text-[#06b6d4] text-[10px] font-medium mb-1 text-center">Step 1 (8%)</p>
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="text-white/40">
                          <th className="text-left py-1">Risk</th>
                          <th className="text-right py-1">Pass</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td className="py-0.5 text-white/50">0.25%</td><td className="text-right text-[#10B981]">100%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.50%</td><td className="text-right text-[#10B981]">100%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.75%</td><td className="text-right text-[#10B981]">98.7%</td></tr>
                        <tr className="bg-[#06b6d4]/10"><td className="py-0.5 text-[#06b6d4]">1.00% <span className="text-[9px]">(Rec.)</span></td><td className="text-right text-[#10B981]">87.9%</td></tr>
                      </tbody>
                    </table>
                  </div>
                  {/* Step 2 */}
                  <div>
                    <p className="text-[#06b6d4] text-[10px] font-medium mb-1 text-center">Step 2 (6%)</p>
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="text-white/40">
                          <th className="text-left py-1">Risk</th>
                          <th className="text-right py-1">Pass</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td className="py-0.5 text-white/50">0.25%</td><td className="text-right text-[#10B981]">100%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.50%</td><td className="text-right text-[#10B981]">100%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.75%</td><td className="text-right text-[#10B981]">98.7%</td></tr>
                        <tr className="bg-[#06b6d4]/10"><td className="py-0.5 text-[#06b6d4]">1.00% <span className="text-[9px]">(Rec.)</span></td><td className="text-right text-[#10B981]">87.9%</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* V2 CFD Tables */}
              <div>
                <h4 className="text-white/70 text-xs font-medium mb-2">V2 · {V2_STATS.label} — {formatNumber(V2_STATS.fiveYear.totalTrades, 0)} trades</h4>
                <div className="grid grid-cols-2 gap-2">
                  {/* Step 1 */}
                  <div>
                    <p className="text-[#06b6d4] text-[10px] font-medium mb-1 text-center">Step 1 (8%)</p>
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="text-white/40">
                          <th className="text-left py-1">Risk</th>
                          <th className="text-right py-1">Pass</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td className="py-0.5 text-white/50">0.25%</td><td className="text-right text-[#10B981]">100%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.50%</td><td className="text-right text-[#10B981]">98.9%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.75%</td><td className="text-right text-[#d97706]">78.9%</td></tr>
                        <tr className="bg-[#06b6d4]/10"><td className="py-0.5 text-[#06b6d4]">1.00% <span className="text-[9px]">(Rec.)</span></td><td className="text-right text-[#EF4444]">33.2%</td></tr>
                      </tbody>
                    </table>
                  </div>
                  {/* Step 2 */}
                  <div>
                    <p className="text-[#06b6d4] text-[10px] font-medium mb-1 text-center">Step 2 (6%)</p>
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="text-white/40">
                          <th className="text-left py-1">Risk</th>
                          <th className="text-right py-1">Pass</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td className="py-0.5 text-white/50">0.25%</td><td className="text-right text-[#10B981]">100%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.50%</td><td className="text-right text-[#10B981]">98.9%</td></tr>
                        <tr><td className="py-0.5 text-white/50">0.75%</td><td className="text-right text-[#d97706]">78.9%</td></tr>
                        <tr className="bg-[#06b6d4]/10"><td className="py-0.5 text-[#06b6d4]">1.00% <span className="text-[9px]">(Rec.)</span></td><td className="text-right text-[#EF4444]">33.2%</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#10B981]" />
              <span className="text-white/50 text-xs">Pass ≥90%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#d97706]" />
              <span className="text-white/50 text-xs">Pass 80-90%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#F59E0B]/30" />
              <span className="text-white/50 text-xs">Highlighted = Recommended</span>
            </div>
          </div>

          {/* Get Funded Calculator */}
          <GetFundedCalculator />

          {/* Mandatory Disclaimer */}
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }}
            className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10"
          >
            <p className="text-white/40 text-xs text-center leading-relaxed">
              <span className="text-white/60 font-medium">Disclaimer:</span> Backtested simulation results based on historical trades. Live trading results will vary and may differ materially. No challenge outcome is guaranteed. This is a statistical study, not a promise of performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Backtest Performance Section - Both Variations */}
      <section className="py-20 section-glow" id="performance" data-testid="orb-performance-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#10B981] text-xs font-mono mb-4">
              BACKTEST RESULTS
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Performance Across Both Variations
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Each variation is independently backtested. Download individual PDF reports from the variation cards above.
            </p>
          </motion.div>

          {/* Per-Variation Performance Cards */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10">
            {ORB_VARIATIONS.map((v) => (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="glass rounded-xl p-4 border border-white/5"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${v.riskColor}20`, color: v.riskColor }}>
                    {v.version}
                  </span>
                  <span className="text-white/40 text-xs">{v.instrument}</span>
                </div>
                <div className="text-center mb-3">
                  <div className="text-[#10B981] font-heading font-bold text-xl">{v.stats.totalReturn}</div>
                  <div className="text-white/40 text-[10px]">Return</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <div className="text-white text-sm font-medium">{v.stats.winRate}</div>
                    <div className="text-white/30 text-[10px]">Win Rate</div>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{v.stats.profitFactor}</div>
                    <div className="text-white/30 text-[10px]">PF</div>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{v.stats.maxDrawdown}</div>
                    <div className="text-white/30 text-[10px]">Max DD</div>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{v.stats.totalTrades}</div>
                    <div className="text-white/30 text-[10px]">Trades</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Sample Screenshot - with note */}
          <div className="mb-6">
            <p className="text-center text-white/40 text-sm mb-4">
              Backtest screenshot for {activeScreenshotVersion}. Each variation has its own detailed PDF report.
            </p>
            
            {/* Version Selector Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {['V1', 'V2'].map((version) => (
                <button
                  key={version}
                  onClick={() => setActiveScreenshotVersion(version)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                    activeScreenshotVersion === version
                      ? 'bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                >
                  {version}
                </button>
              ))}
            </div>

            {/* Screenshot Type Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {screenshotTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveScreenshot(tab.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeScreenshot === tab.id
                      ? 'bg-[#7c3aed] text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon size={14} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <motion.div 
            key={`${activeScreenshotVersion}-${activeScreenshot}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl max-w-4xl mx-auto"
          >
            <div className="absolute top-0 left-0 right-0 h-8 bg-[#1a1a2e] flex items-center px-3 gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-3 text-white/40 text-xs">Strategy Backtest — Sample ({activeScreenshotVersion})</span>
            </div>
            <img 
              src={SCREENSHOTS[activeScreenshotVersion]?.[activeScreenshot] || SCREENSHOTS.V1[activeScreenshot]} 
              alt={`${activeScreenshot} screenshot - ${activeScreenshotVersion}`}
              className="w-full pt-8"
              loading="lazy"
            />
          </motion.div>

          {/* Download Reports CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-white/50 text-sm mb-4">
              Each variation includes a downloadable PDF with complete backtest data
            </p>
            
            <a 
              href="#variations"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm font-medium transition-all"
            >
              <FileText size={16} className="text-[#7c3aed]" />
              View All Backtest Reports
              <ArrowRight size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Monthly P&L Breakdown - All Variations */}
      <section className="py-20 bg-gradient-to-b from-transparent via-[#06b6d4]/5 to-transparent" id="monthly-pnl" data-testid="orb-monthly-pnl-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#06b6d4] text-xs font-mono mb-4">
              <Calendar className="inline w-3 h-3 mr-1" />
              MONTHLY BREAKDOWN
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Month-by-Month P&L
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Real monthly profit/loss data extracted from backtest reports. See how each variation performs over time.
            </p>
          </motion.div>

          {/* Variation Selector Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { id: 'V1', label: 'V1 - Conservative' },
              { id: 'V2', label: 'V2 - Growth' },
            ].map((variation) => (
              <button
                key={variation.id}
                onClick={() => setSelectedPnlVariation(variation.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPnlVariation === variation.id
                    ? 'bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {variation.label}
              </button>
            ))}
          </div>

          {/* Monthly P&L Grid */}
          <motion.div
            key={selectedPnlVariation}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-13 gap-2"
          >
            {MONTHLY_PNL_DATA[selectedPnlVariation]?.map((month, index) => (
              <motion.div
                key={month.month}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className={`glass rounded-lg p-3 text-center border ${
                  month.profit >= 0 ? 'border-[#10B981]/20' : 'border-[#EF4444]/20'
                }`}
              >
                <div className="text-white/40 text-[10px] font-mono mb-1">{month.month}</div>
                <div className={`font-heading font-bold text-sm ${
                  month.profit >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'
                }`}>
                  {month.profit >= 0 ? '+' : ''}${Math.abs(month.profit).toFixed(0)}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Summary Stats for Selected Variation */}
          {(() => {
            const currentVariation = ORB_VARIATIONS.find(v => v.version === selectedPnlVariation);
            const stats = currentVariation?.stats || {};
            return (
              <div className="mt-8">
                {/* Trade Statistics Row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="glass rounded-xl p-4 text-center border border-[#10B981]/20">
                    <div className="text-[#10B981] font-heading font-bold text-lg">
                      +${stats.grossProfit?.toLocaleString() || '0'}
                    </div>
                    <div className="text-white/50 text-xs">Gross Profit</div>
                    <div className="text-[#10B981]/60 text-[10px] mt-1">{stats.winningTrades || 0} winning trades</div>
                  </div>
                  <div className="glass rounded-xl p-4 text-center border border-[#EF4444]/20">
                    <div className="text-[#EF4444] font-heading font-bold text-lg">
                      -${stats.grossLoss?.toLocaleString() || '0'}
                    </div>
                    <div className="text-white/50 text-xs">Gross Loss</div>
                    <div className="text-[#EF4444]/60 text-[10px] mt-1">{stats.losingTrades || 0} losing trades</div>
                  </div>
                  <div className="glass rounded-xl p-4 text-center border border-[#7c3aed]/20">
                    <div className="text-[#7c3aed] font-heading font-bold text-lg">
                      {stats.netProfit || '+$0'}
                    </div>
                    <div className="text-white/50 text-xs">Net Profit</div>
                    <div className="text-[#7c3aed]/60 text-[10px] mt-1">{stats.totalTrades || 0} total trades</div>
                  </div>
                  <div className="glass rounded-xl p-4 text-center border border-[#06b6d4]/20">
                    <div className="text-[#06b6d4] font-heading font-bold text-lg">
                      {stats.winRate || '0%'}
                    </div>
                    <div className="text-white/50 text-xs">Win Rate</div>
                    <div className="text-[#06b6d4]/60 text-[10px] mt-1">{stats.winningTrades}/{stats.totalTrades} trades</div>
                  </div>
                  <div className="glass rounded-xl p-4 text-center border border-[#F59E0B]/20">
                    <div className="text-[#F59E0B] font-heading font-bold text-lg">
                      {MONTHLY_PNL_DATA[selectedPnlVariation]?.filter(m => m.profit >= 0).length}/{MONTHLY_PNL_DATA[selectedPnlVariation]?.length}
                    </div>
                    <div className="text-white/50 text-xs">Profitable Months</div>
                    <div className="text-[#F59E0B]/60 text-[10px] mt-1">Every calendar month</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Note */}
          <p className="text-center text-white/40 text-xs mt-6">
            Monthly P&L data extracted from backtest reports. Download full PDFs from the variation cards above.
          </p>
          <p className="text-center text-white/30 text-[10px] mt-2">
            Backtested results. Live trading results will vary.
          </p>
        </div>
      </section>

      <LiveBacktestVideo />

      {/* ========== VERIFY IT YOURSELF SECTION ========== */}
      <section className="py-20 bg-gradient-to-b from-transparent via-[#F59E0B]/5 to-transparent" id="verify" data-testid="orb-verify-section">
        <div className="max-w-5xl mx-auto px-6">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-mono mb-4">
              <Shield size={14} />
              VERIFY IT YOURSELF
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Don't trust our screenshots.<br/>
              <span className="text-[#F59E0B]">Run the backtest yourself.</span>
            </h2>
            <p className="text-white/60 text-lg max-w-3xl mx-auto">
              Download the free Monk Algo ORB tester and run the backtest on your own NinjaTrader 8 — in minutes.
            </p>
          </motion.div>

          {/* Three Columns: What / Why / How */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* WHAT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-[#F59E0B]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
                  <Package size={20} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white">What it is</h3>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                A <span className="text-white font-medium">verification-only build</span> of the Monk Algo ORB for NinjaTrader 8. It runs inside NinjaTrader's Strategy Analyzer and lets you run the backtest behind the results above (V1 Conservative + V2 Growth, MNQ, 5m).
              </p>
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 text-xs flex items-start gap-2">
                  <Lock size={14} className="flex-shrink-0 mt-0.5" />
                  <span>It is <strong>NOT</strong> the live product: it only backtests, and is hard-locked so it cannot place a single live or demo trade — Strategy Analyzer only.</span>
                </p>
              </div>
            </motion.div>

            {/* WHY */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 border border-[#F59E0B]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
                  <Target size={20} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white">Why we built it</h3>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-3">
                "Trust me, it works" isn't good enough in trading — the space is full of faked screenshots. So instead of asking you to believe our numbers, we hand you the engine and let you regenerate them.
              </p>
              <p className="text-white/60 text-sm leading-relaxed">
                Every stat on this page comes from this exact strategy; the tester lets you confirm the trades, win rate, drawdown and profit factor are real, <span className="text-[#F59E0B] font-medium">with your own eyes</span>.
              </p>
            </motion.div>

            {/* HOW - Steps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 border border-[#F59E0B]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
                  <BookOpen size={20} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white">How it works</h3>
              </div>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-white/60">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs flex items-center justify-center font-bold">1</span>
                  Download the tester files below
                </li>
                <li className="flex items-start gap-2 text-white/60">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs flex items-center justify-center font-bold">2</span>
                  Open NinjaTrader 8 → Strategy Analyzer
                </li>
                <li className="flex items-start gap-2 text-white/60">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs flex items-center justify-center font-bold">3</span>
                  Strategy: MonkAlgoOrb_V1 or V2 Tester
                </li>
                <li className="flex items-start gap-2 text-white/60">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs flex items-center justify-center font-bold">4</span>
                  Instrument: MNQ | 5-minute
                </li>
                <li className="flex items-start gap-2 text-white/60">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs flex items-center justify-center font-bold">5</span>
                  Run backtest → Check the results
                </li>
              </ol>
            </motion.div>
          </div>

          {/* Additional Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10 glass rounded-xl p-5 text-sm text-white/50"
          >
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <p><Settings size={14} className="inline mr-1.5 text-[#F59E0B]" /> <strong className="text-white/70">You CAN change:</strong> Deposit (capital) and Risk Per Trade</p>
              <p><Lock size={14} className="inline mr-1.5 text-white/40" /> <strong className="text-white/70">Locked:</strong> Symbol, timeframe, timezone, strategy logic</p>
            </div>
          </motion.div>

          {/* Download CTA - NinjaTrader 8 tester */}
          <div className="max-w-2xl mx-auto">
            {/* NinjaTrader 8 Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-[#06b6d4]/30"
              data-testid="tester-card-nt8"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#06b6d4]/20 flex items-center justify-center">
                  <LineChart size={24} className="text-[#06b6d4]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-white">NinjaTrader 8 Tester</h3>
                  <p className="text-[#06b6d4] text-xs font-medium">MNQ Futures · Strategy Analyzer</p>
                </div>
              </div>
              
              <div className="space-y-3 mb-4 text-sm text-white/60">
                <p className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#06b6d4]/20 text-[#06b6d4] text-xs flex items-center justify-center font-bold">1</span>
                  Download and install the strategy files
                </p>
                <p className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#06b6d4]/20 text-[#06b6d4] text-xs flex items-center justify-center font-bold">2</span>
                  Open NinjaTrader 8 → Strategy Analyzer
                </p>
                <p className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#06b6d4]/20 text-[#06b6d4] text-xs flex items-center justify-center font-bold">3</span>
                  Select MonkAlgoOrb_V1 or V2 Tester
                </p>
                <p className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#06b6d4]/20 text-[#06b6d4] text-xs flex items-center justify-center font-bold">4</span>
                  Instrument: MNQ | 5-minute | Run backtest
                </p>
              </div>
              
              <div className="mb-5">
                <p className="text-white/70 text-xs font-medium mb-2">Watch the NinjaTrader 8 tester guide</p>
                <YouTubeVideo url={ORB_VIDEOS.nt8Guide} title="NinjaTrader 8 tester guide: Monk Algo ORB" accent="#06b6d4" testId="nt8-tester-guide-video" thumbVersion={ORB_VIDEOS.thumbVersion} />
              </div>
              
              {/* Expected Results placeholder - hidden until provided */}
              {/* <div className="mb-4 p-3 rounded-lg bg-[#06b6d4]/10 border border-[#06b6d4]/20">
                <p className="text-[#06b6d4] text-xs font-medium">Expected Results</p>
                <p className="text-white/50 text-xs">Placeholder for expected results</p>
              </div> */}
              
              {/* GMT Guide placeholder - hidden until provided */}
              {/* <a href="#" className="text-[#06b6d4] text-xs hover:underline mb-4 block">📄 GMT Timezone Guide</a> */}
              
              <button
                onClick={() => setShowTesterModal('nt8')}
                className="w-full py-3 bg-gradient-to-r from-[#06b6d4] to-[#0891b2] hover:opacity-90 text-white font-heading font-semibold text-sm rounded-xl flex items-center justify-center gap-2 transition-all"
                data-testid="download-nt8-btn"
              >
                <Download size={18} />
                Download NinjaTrader 8 Tester
              </button>
            </motion.div>

          </div>
          
          <p className="mt-6 text-center text-white/40 text-xs">
            The tester includes V1 + V2 files and a step-by-step PDF guide
          </p>
          
          {/* Book a Call CTA - End of Tester Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <button
              onClick={handleBookCall}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:border-[#7c3aed]/30 hover:bg-[#7c3aed]/10 hover:text-white transition-all text-sm"
              data-testid="tester-book-call-btn"
            >
              <Video size={16} />
              Need help? Book a call with Sahil
              <ArrowRight size={14} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Tester Lead Capture Modal */}
      <TesterLeadModal 
        isOpen={!!showTesterModal} 
        onClose={() => setShowTesterModal(null)}
        platform="nt8"
      />

      {/* Social Proof Bar */}
      <section className="py-4 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 text-center">
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-[#7c3aed]" />
              <span className="text-white/60 text-sm">2 ORB Variations</span>
            </div>
            <div className="flex items-center gap-2">
              <Bot size={16} className="text-[#06b6d4]" />
              <span className="text-white/60 text-sm">MT5 + NinjaTrader 8</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-[#10B981]" />
              <span className="text-white/60 text-sm">Up to +{V2_STATS.oneYear.totalReturn}% Backtested Return</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#F59E0B]" />
              <span className="text-white/60 text-sm">2.51% Min Drawdown</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Monk Algo ORB Section */}
      <section className="py-20" id="why" data-testid="orb-why-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-[#7c3aed] text-xs font-mono mb-4">
              <HelpCircle size={14} />
              WHY MULTIPLE VARIATIONS?
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              One Algorithm Can't Suit Every Trader
            </h2>
            <p className="text-white/50 text-lg max-w-3xl mx-auto">
              Different traders have different goals, risk tolerances, and trading styles. Instead of forcing everyone into one configuration, Monk Algo ORB provides multiple professionally optimized variations.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {WHY_VARIATIONS.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-5 border border-white/5"
              >
                <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/15 flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-[#7c3aed]" />
                </div>
                <h3 className="font-heading font-semibold text-white text-base mb-2">{item.title}</h3>
                <p className="text-white/50 text-xs mb-2">{item.description}</p>
                <p className="text-[#06b6d4] text-xs font-medium">{item.solution}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ORB Variations Section - Scalable Grid */}
      <section className="py-20 bg-gradient-to-b from-transparent via-[#7c3aed]/5 to-transparent" id="variations" data-testid="orb-variations-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-[#06b6d4] text-xs font-mono mb-4">
              <Layers size={14} />
              ALL VARIATIONS INCLUDED
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Choose Your ORB Variation
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Every subscription includes access to both variations with full backtest reports. Switch between them anytime.
            </p>
          </motion.div>

          {/* Variations Grid - 2 cards centered */}
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {ORB_VARIATIONS.map((variation, index) => (
              <OrbVariationCard key={variation.id} variation={variation} index={index} />
            ))}
          </div>

          {/* Future Variations Teaser */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <Gift size={16} className="text-[#7c3aed]" />
              <span className="text-white/60 text-sm">
                Future variations (V3, V4...) included with your subscription at no extra cost
              </span>
            </div>
          </motion.div>

          {/* Quick Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 overflow-x-auto"
          >
            <h3 className="font-heading font-semibold text-white text-xl text-center mb-6">
              Quick Comparison — Find Your Perfect Variation
            </h3>
            <table className="w-full min-w-[600px] border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/60 text-xs font-medium p-3">Variation</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Instrument</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Return</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Win Rate</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Profit Factor</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Max DD</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Trades</th>
                  <th className="text-center text-white/60 text-xs font-medium p-3">Best For</th>
                </tr>
              </thead>
              <tbody>
                {ORB_VARIATIONS.map((v) => (
                  <tr key={v.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${v.riskColor}20`, color: v.riskColor }}>{v.version}</span>
                        <span className="text-white text-sm font-medium">{v.name.replace('Monk Algo ORB ', '')}</span>
                      </div>
                    </td>
                    <td className="text-center text-white/70 text-sm p-3">{v.instrument}</td>
                    <td className="text-center text-[#10B981] font-semibold text-sm p-3">{v.stats.totalReturn}</td>
                    <td className="text-center text-[#06b6d4] text-sm p-3">{v.stats.winRate}</td>
                    <td className="text-center text-[#8B5CF6] font-semibold text-sm p-3">{v.stats.profitFactor}</td>
                    <td className="text-center text-[#F59E0B] text-sm p-3">{v.stats.maxDrawdown}</td>
                    <td className="text-center text-white/60 text-sm p-3">{v.stats.totalTrades}</td>
                    <td className="text-center text-white/50 text-xs p-3">{v.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20" id="included" data-testid="orb-included-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-[#10B981] text-xs font-mono mb-4">
              <Package size={14} />
              COMPLETE PACKAGE
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Everything You Get
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              One subscription unlocks the complete Monk Algo ORB ecosystem.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {WHATS_INCLUDED.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center gap-3 p-4 rounded-xl border ${
                  item.highlight 
                    ? 'bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border-[#7c3aed]/30' 
                    : 'bg-white/5 border-white/5'
                }`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${item.highlight ? 'text-[#7c3aed]' : 'text-[#06b6d4]'}`} />
                <span className={`text-sm font-medium ${item.highlight ? 'text-white' : 'text-white/70'}`}>
                  {item.text}
                </span>
                {item.highlight && <Check size={16} className="text-[#10B981] ml-auto" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prop Firm Compatible Section */}
      <section className="py-20 relative overflow-hidden" data-testid="orb-prop-firm-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7c3aed]/10 via-transparent to-[#06b6d4]/10" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border border-[#7c3aed]/30 text-[#7c3aed] text-xs font-mono mb-4">
              <Building2 size={14} />
              PROP FIRM READY
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-white mb-4">
              Pass Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7c3aed] to-[#06b6d4]">Prop Firm Challenge</span>
            </h2>
            <p className="text-white/60 text-lg max-w-3xl mx-auto">
              Whether you trade forex/CFD or futures prop firms, Monk Algo ORB's low-drawdown design fits both. Run the MT5 EA on CFD firms, or use NinjaTrader 8 for futures prop firms with direct execution.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#10B981] font-heading font-bold text-3xl mb-1">2.51%</div>
              <div className="text-white/50 text-sm">V1 Max Drawdown</div>
              <div className="text-[#10B981] text-xs mt-1">Ultra-low risk</div>
            </div>
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#7c3aed] font-heading font-bold text-3xl mb-1">2.93</div>
              <div className="text-white/50 text-sm">Profit Factor</div>
              <div className="text-[#7c3aed] text-xs mt-1">Best of all variations</div>
            </div>
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#06b6d4] font-heading font-bold text-3xl mb-1">+45.60%</div>
              <div className="text-white/50 text-sm">V1 Return</div>
              <div className="text-[#06b6d4] text-xs mt-1">Backtest</div>
            </div>
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#F59E0B] font-heading font-bold text-3xl mb-1">108</div>
              <div className="text-white/50 text-sm">Total Trades</div>
              <div className="text-[#F59E0B] text-xs mt-1">Statistical validity</div>
            </div>
          </div>

          {/* Two-Column Prop Firm Categories */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Forex/CFD Prop Firms */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 border border-[#10B981]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                  <Bot size={24} className="text-[#10B981]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">Forex / CFD Prop Firms</h3>
                  <p className="text-[#10B981] text-sm font-medium">MT5 Expert Advisor</p>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Our MT5 Expert Advisor runs directly on MetaTrader 5. These firms typically offer 2-step challenges with a 10% max drawdown limit. V1's ultra-low 2.51% drawdown sits comfortably inside this limit.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Blueberry Funded', 'The5ers'].map((firm) => (
                  <span key={firm} className="px-3 py-1.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-medium border border-[#10B981]/20">
                    {firm}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Shield size={14} />
                <span>10% max drawdown limit — V1 uses only 2.51%</span>
              </div>
            </motion.div>

            {/* Futures Prop Firms */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 border border-[#06b6d4]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#06b6d4]/20 flex items-center justify-center">
                  <LineChart size={24} className="text-[#06b6d4]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">Futures Prop Firms</h3>
                  <p className="text-[#06b6d4] text-sm font-medium">NinjaTrader 8 → Direct Execution</p>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Our NinjaTrader 8 strategy enables direct automated execution on futures prop accounts. Futures firms enforce stricter ~4% max drawdown — our algo stays within this limit.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Tradeify', 'Lucid Trading', 'FundedNext Futures'].map((firm) => (
                  <span key={firm} className="px-3 py-1.5 rounded-full bg-[#06b6d4]/10 text-[#06b6d4] text-xs font-medium border border-[#06b6d4]/20">
                    {firm}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Shield size={14} />
                <span>~4% max drawdown limit — V1 uses only 2.51%</span>
              </div>
            </motion.div>
          </div>

          {/* Disclaimer */}
          <p className="text-center text-white/30 text-xs mt-8">
            Prop firm compatibility based on drawdown requirements. Results not guaranteed — always verify firm-specific rules before trading.
          </p>
        </div>
      </section>

      {/* Book a Call Section - Above Pricing */}
      <BookCallSection onBookCall={handleBookCall} />

      {/* Pricing Section */}
      <section className="py-20 section-glow" id="pricing" data-testid="orb-pricing-section">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border border-[#7c3aed]/30 text-[#06b6d4] text-xs font-mono mb-4">
              <Zap size={14} />
              CHOOSE YOUR PLAN
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Get Monk Algo ORB
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              One subscription. Both variations. Both platforms. Future updates included.
            </p>
            {/* See Live Results Button */}
            <div className="mt-6">
              <a 
                href="https://t.me/+9yeoU7R5h3k2NDVl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0088cc]/20 border border-[#0088cc]/40 text-[#0088cc] font-heading font-medium text-sm hover:bg-[#0088cc]/30 transition-all"
                data-testid="see-live-results-btn"
              >
                <Send size={16} />
                See Live Results on Telegram
                <ArrowRight size={14} />
              </a>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            
            {/* QUARTERLY PLAN */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl border border-white/10 bg-[#0a0a12]/80 backdrop-blur-sm overflow-hidden"
            >
              <div className="p-6 md:p-8">
                {/* Plan Name */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-xl text-white">Quarterly</h3>
                </div>

                {/* Pricing */}
                <div className="mb-2">
                  <span className="font-heading font-bold text-4xl text-white">₹49,997</span>
                </div>
                <p className="text-white/40 text-sm mb-1">for 3 months</p>
                <p className="text-white/30 text-xs mb-2">Base price for 2 platform activations</p>
                <p className="text-[#10B981] text-xs font-semibold mb-6" data-testid="pricing-quarterly-addon-discount">All Additional Licenses: 50% OFF</p>

                {/* License Count - Prominent */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#7c3aed]/10 border border-[#7c3aed]/20 mb-4">
                  <Users className="w-5 h-5 text-[#7c3aed]" />
                  <span className="text-white font-semibold">2 Platform Activations</span>
                </div>
                <p className="text-white/40 text-xs mb-6">MT5 and/or NinjaTrader 8 — you choose the split</p>

                {/* Features Checklist */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">Both ORB variations (V1–V2)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">MT5 EA + NinjaTrader 8 Strategy</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">All updates & new variations during 3 months</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">Full backtest reports included</span>
                  </div>
                </div>

                {/* CTA Button */}
                <button 
                  onClick={() => handleSubscribe('quarterly')}
                  className="w-full py-4 rounded-xl border border-[#7c3aed]/50 text-white font-heading font-semibold hover:bg-[#7c3aed]/10 transition-all flex items-center justify-center gap-2"
                  data-testid="orb-quarterly-btn"
                >
                  Get Started
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>

            {/* YEARLY PLAN - HIGHLIGHTED */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden"
            >
              {/* Glowing Border Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#7c3aed] via-[#06b6d4] to-[#7c3aed] rounded-2xl p-[2px]">
                <div className="absolute inset-[2px] bg-[#0a0a12] rounded-2xl" />
              </div>
              
              {/* Best Value Ribbon */}
              <div className="absolute -right-12 top-6 rotate-45 z-10">
                <div className="bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white text-xs font-bold py-1 px-12 shadow-lg">
                  BEST VALUE
                </div>
              </div>

              <div className="relative z-10 p-6 md:p-8">
                {/* Plan Name */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-xl text-white">Yearly</h3>
                  <span className="text-[#06b6d4] text-xs font-medium">RECOMMENDED</span>
                </div>

                {/* Pricing */}
                <div className="mb-2">
                  <span className="text-white/40 line-through text-lg mr-2">₹1,99,988</span>
                  <span className="font-heading font-bold text-4xl text-transparent bg-clip-text bg-gradient-to-r from-[#7c3aed] to-[#06b6d4]">₹1,49,997</span>
                </div>
                <p className="text-white/40 text-sm mb-1">for 12 months</p>
                <p className="text-[#06b6d4] text-xs font-medium mb-2">Save ₹50,000 (25%) vs quarterly — 1 quarter free!</p>
                <p className="text-[#10B981] text-xs font-semibold mb-6" data-testid="pricing-yearly-addon-discount">All Additional Licenses: 50% OFF</p>

                {/* License Count - Prominent */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border border-[#06b6d4]/30 mb-4">
                  <Users className="w-5 h-5 text-[#06b6d4]" />
                  <span className="text-white font-semibold">2 Platform Activations</span>
                </div>
                <p className="text-white/40 text-xs mb-6">MT5 and/or NinjaTrader 8 — you choose the split</p>

                {/* Features Checklist */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">Both ORB variations (V1–V2)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">MT5 EA + NinjaTrader 8 Strategy</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">All updates, upgrades & new variations for 12 months</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check size={16} className="text-[#06b6d4] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm">Full backtest reports + <span className="text-[#06b6d4] font-medium">priority support</span></span>
                  </div>
                </div>

                {/* CTA Button */}
                <button 
                  onClick={() => handleSubscribe('yearly')}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white font-heading font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#7c3aed]/30 hover:shadow-[#7c3aed]/50 hover:scale-[1.02]"
                  data-testid="orb-yearly-btn"
                >
                  <Zap size={18} />
                  Get Best Value
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          </div>

          {/* Payment & Security Info */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-6 mt-8 text-white/30 text-xs"
          >
            <div className="flex items-center gap-2">
              <Shield size={14} />
              <span>Secure payment via Razorpay</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap size={14} />
              <span>Instant access after payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Headphones size={14} />
              <span>Dedicated support included</span>
            </div>
          </motion.div>
          
          {/* Book a Call CTA - Below Pricing */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <button
              onClick={handleBookCall}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:border-[#7c3aed]/30 hover:bg-[#7c3aed]/10 hover:text-white transition-all text-sm"
              data-testid="pricing-book-call-btn"
            >
              <Video size={16} />
              Have questions? Book a call with Sahil
              <ArrowRight size={14} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" id="faq" data-testid="orb-faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-[#F59E0B] text-xs font-mono mb-4">
              <HelpCircle size={14} />
              FAQ
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Common Questions
            </h2>
          </motion.div>

          <div className="space-y-3">
            {FAQ_DATA.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-xl border border-white/5 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white text-sm pr-4">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp size={18} className="text-[#7c3aed] flex-shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-white/40 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="px-5 pb-5"
                  >
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Won't Claim Section */}
      <section className="py-12" data-testid="orb-transparency-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/5"
          >
            <h3 className="font-heading font-semibold text-lg text-white/80 mb-4">What we won't claim</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              No guaranteed profits. No 90% win-rate marketing. Every number on this page is a simulation from backtested trades, with the methodology published. Live results, including losing days, are posted on our <a href="https://t.me/+9yeoU7R5h3k2NDVl" target="_blank" rel="noopener noreferrer" className="text-[#06b6d4] hover:underline">Telegram channel</a>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Risk Disclaimer */}
      <section className="py-8 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-500 font-semibold text-sm mb-1">Risk Disclaimer</h4>
              <p className="text-white/40 text-xs leading-relaxed">
                Trading forex, CFDs, and other financial instruments involves significant risk of loss. Past performance is not indicative of future results. 
                The backtested results shown do not guarantee future performance. 
                Only trade with capital you can afford to lose. This is a tool to assist your trading, not financial advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA */}
      <div className="fixed bottom-6 right-6 z-50 hidden md:block">
        <motion.a
          href="#pricing"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 2 }}
          className="bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white font-heading font-semibold px-6 py-3 rounded-full flex items-center gap-2 shadow-lg shadow-[#7c3aed]/30 hover:shadow-[#7c3aed]/50 hover:scale-105 transition-all"
          data-testid="orb-sticky-cta"
        >
          <Zap size={18} />
          Get All 2 Variations
        </motion.a>
      </div>

      {/* Subscribe Modal */}
      <OrbAlgoSubscribeModal 
        isOpen={showSubscribeModal} 
        onClose={() => setShowSubscribeModal(false)}
        selectedPlan={selectedPlan}
      />
      
      {/* Book Call Modal */}
      <BookCallModal 
        isOpen={showBookCallModal} 
        onClose={() => setShowBookCallModal(false)} 
      />
    </div>
  );
}
